import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import api, { formatApiError, getWsUrl } from "@/lib/api";
import logger from "@/lib/logger";
import { Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import EmojiPicker from "@/components/EmojiPicker";
import RichTextInput from "@/components/RichTextInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { useUnread } from "@/context/UnreadContext";
import { usePresence } from "@/context/PresenceContext";
import { FormatToolbar, ShortcodeMenu, detectShortcode, searchShortcodes, replaceShortcode, hasFormatting, handleFormatShortcut } from "@/components/ChatComposerKit";
import AnnotationModal from "@/components/AnnotationModal";
import {
  Plus, Send, Hash, User as UserIcon, Download, Pencil, Trash2, ArrowLeft,
  Paperclip, X, FileText, Pin, CornerUpLeft, Eye, EyeOff, Archive, ArchiveRestore, Loader2,
  SmilePlus, Search, Users, Check, Lock, Folder, ChevronDown, ChevronRight, CheckSquare,
  Link as LinkIcon, Bell, BellOff, ArrowDownCircle, Bookmark, BookmarkCheck, Star, StarOff, CheckCheck, AtSign,
} from "lucide-react";
import linkify from "@/lib/linkify";
import { RelativeTime, ChatDateDivider, sameDay } from "@/lib/chatTime";
import { toast } from "sonner";

// Slack-style emoji reactions — fixed common set (mirrors backend ALLOWED_REACTIONS).
export const REACTION_EMOJIS = ["👍", "❤️", "😄", "🎉", "✅", "👀"];

// Resolve a stored avatar (relative /api/files path or absolute URL) to a src.
export const resolveAvatar = (u) =>
  (u && u.startsWith("/api/files/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u) || "";

function initialsOf(name) {
  const p = (name || "?").trim().split(/\s+/);
  return (((p[0] || "")[0] || "") + ((p[1] || "")[0] || "")).toUpperCase() || "?";
}
function avatarHue(seed = "") {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return h;
}
// Small round user photo with a coloured-initials fallback (no photo / load error).
// Pass `userId` to overlay a live presence dot (green online / grey offline).
export function MiniAvatar({ src, name, size = 22, userId = null }) {
  const [err, setErr] = useState(false);
  const { isOnline } = usePresence();
  const dim = { width: size, height: size };
  const dotSize = Math.max(7, Math.round(size * 0.34));
  const online = userId ? isOnline(userId) : false;
  const inner = (src && !err) ? (
    <img src={src} alt={name || ""} onError={() => setErr(true)} style={dim}
      className="rounded-full object-cover shrink-0 border border-slate-200" />
  ) : (
    <span style={{ ...dim, background: `hsl(${avatarHue(name || "?")} 52% 45%)` }}
      className="rounded-full shrink-0 inline-flex items-center justify-center text-white font-semibold text-[10px] leading-none select-none"
      aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
  if (!userId) return inner;
  return (
    <span className="relative inline-flex shrink-0" data-testid={`avatar-presence-${userId}`}>
      {inner}
      <span
        style={{ width: dotSize, height: dotSize }}
        className={`absolute -bottom-0.5 -right-0.5 rounded-full ring-2 ring-white ${online ? "bg-emerald-500" : "bg-slate-300"}`}
        title={online ? "Online" : "Offline"}
        data-testid={`presence-dot-${userId}${online ? "-online" : "-offline"}`}
      />
    </span>
  );
}

export function renderText(text, mentionIds, currentUserId) {
  // Render @-mentions as styled chips. We bold every "@Word" that matches a
  // mentioned user's first/full name. The persisted `mentions` array on the
  // message is the source of truth — text matching is just for visual styling.
  if (!text) return null;
  if (!mentionIds || mentionIds.length === 0) return text;
  const parts = text.split(/(@\w[\w\s]*?)(?=\s|$|[,.!?])/g);
  return parts.map((p, i) => {
    if (p.startsWith("@")) {
      const isMe = mentionIds.includes(currentUserId);
      return (
        <span key={`mention-${i}-${p}`} className={`px-1 rounded-sm font-medium ${isMe ? "bg-[#F54800]/15 text-[#B7350A]" : "bg-blue-50 text-blue-700"}`}>
          {p}
        </span>
      );
    }
    return p;
  });
}

// Inline formatting inside a single line: **bold**, *italic*, ~~strike~~,
// `code`, and @mentions. Used by renderRich for each line / list item.
export function renderInline(text, mentionIds, currentUserId, kp = "") {
  if (!text) return null;
  const tokenRe = /(\*\*[^*\n]+\*\*|~~[^~\n]+~~|\+\+(?!\s)[^+\n]+?(?<!\s)\+\+|`[^`\n]+`|\*[^*\n]+\*|@\w[\w ]*?(?=[\s,.!?]|$))/g;
  const out = [];
  let last = 0, m, i = 0;
  const pushText = (seg) => { if (seg) out.push(<React.Fragment key={`${kp}t${i}`}>{linkify(seg)}</React.Fragment>); };
  while ((m = tokenRe.exec(text)) !== null) {
    if (m.index > last) pushText(text.slice(last, m.index));
    const t = m[0];
    if (t.startsWith("**")) out.push(<strong key={`${kp}b${i}`}>{t.slice(2, -2)}</strong>);
    else if (t.startsWith("~~")) out.push(<span key={`${kp}s${i}`} className="line-through">{t.slice(2, -2)}</span>);
    else if (t.startsWith("++")) out.push(<u key={`${kp}u${i}`}>{t.slice(2, -2)}</u>);
    else if (t.startsWith("`")) out.push(<code key={`${kp}c${i}`} className="px-1 py-0.5 rounded bg-black/10 text-inherit text-[0.85em]">{t.slice(1, -1)}</code>);
    else if (t.startsWith("@")) {
      const kw = t.slice(1).trim().toLowerCase();
      const isBroadcast = kw === "here" || kw === "channel" || kw === "everyone" || kw === "all";
      const isMe = (mentionIds || []).includes(currentUserId);
      const cls = isBroadcast
        ? "bg-amber-100 text-amber-800"
        : isMe ? "bg-[#F54800]/15 text-[#B7350A]" : "bg-blue-50 text-blue-700";
      out.push(<span key={`${kp}m${i}`} className={`px-1 rounded-sm font-medium ${cls}`}>{t}</span>);
    } else out.push(<em key={`${kp}i${i}`}>{t.slice(1, -1)}</em>);
    last = m.index + t.length;
    i++;
  }
  if (last < text.length) pushText(text.slice(last));
  return out;
}

// Block formatting: groups "- "/"* "/"• " lines into bullet lists and
// "1." / "1)" lines into numbered lists; everything else stays as text with
// line breaks preserved. @mentions + inline styles work inside every line.
const BULLET_RE = /^\s*[-*\u2022]\s+(.*)$/;
const NUM_RE = /^\s*\d+[.)]\s+(.*)$/;
export function renderRich(text, mentionIds, currentUserId) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let i = 0, k = 0;
  while (i < lines.length) {
    const bm = BULLET_RE.exec(lines[i]);
    const nm = NUM_RE.exec(lines[i]);
    if (bm) {
      const items = [];
      while (i < lines.length) { const mm = BULLET_RE.exec(lines[i]); if (!mm) break; items.push(mm[1]); i++; }
      blocks.push(<ul key={`ul${k++}`}>{items.map((it, j) => <li key={j}>{renderInline(it, mentionIds, currentUserId, `ul${k}_${j}_`)}</li>)}</ul>);
      continue;
    }
    if (nm) {
      const items = [];
      while (i < lines.length) { const mm = NUM_RE.exec(lines[i]); if (!mm) break; items.push(mm[1]); i++; }
      blocks.push(<ol key={`ol${k++}`}>{items.map((it, j) => <li key={j}>{renderInline(it, mentionIds, currentUserId, `ol${k}_${j}_`)}</li>)}</ol>);
      continue;
    }
    const buf = [];
    while (i < lines.length && !BULLET_RE.test(lines[i]) && !NUM_RE.test(lines[i])) { buf.push(lines[i]); i++; }
    blocks.push(
      <div key={`p${k++}`} className="whitespace-pre-wrap">
        {buf.map((ln, j) => <React.Fragment key={j}>{j > 0 && <br />}{renderInline(ln, mentionIds, currentUserId, `p${k}_${j}_`)}</React.Fragment>)}
      </div>
    );
  }
  return blocks;
}

export function AttachmentView({ att, onAnnotate, onOpen }) {
  if (!att) return null;
  if (att.type === "image") {
    return (
      <div className="mt-1.5 relative group inline-block" data-testid="chat-attachment-image">
        <button type="button" onClick={() => onOpen && onOpen(att)} className="block cursor-zoom-in" title="Click to preview" data-testid="attachment-open">
          <img src={att.url} alt={att.name} className="max-w-[240px] max-h-[220px] rounded-sm border border-slate-200 object-cover" />
        </button>
        {onAnnotate && (
          <button
            type="button"
            onClick={() => onAnnotate(att)}
            className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 bg-white/95 border border-slate-200 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            data-testid="annotate-btn"
          >
            <Pencil size={10} /> Annotate
          </button>
        )}
      </div>
    );
  }
  const human = att.size ? (att.size >= 1048576 ? `${(att.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(att.size / 1024))} KB`) : null;
  return (
    <a
      href={att.url}
      target="_blank"
      rel="noreferrer"
      download={att.name}
      className="mt-1.5 inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-100 max-w-[260px]"
      data-testid="chat-attachment-file"
    >
      <FileText size={13} className="text-[#F54800] shrink-0" />
      <span className="truncate">{att.name}</span>
      {human && <span className="text-slate-400 shrink-0">· {human}</span>}
      <Download size={12} className="text-slate-400 shrink-0" />
    </a>
  );
}

export function ChatMessage({ msg, mine, currentUserId, quoted, canShare, onAnnotate, onPin, onSave, saved, onShareToggle, onPushUpdate, onReply, onReact, onEdit, onCopyLink, userNameById, userAvatarById, onOpenImage, onConvertTask }) {
  const taggedMe = !mine && (msg.mentions || []).includes(currentUserId);
  const atts = msg.attachments || [];
  const [pickerOpen, setPickerOpen] = useState(false);
  const [fullEmojiOpen, setFullEmojiOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.text || "");
  const editable = mine && !msg.is_system && msg.sender_id !== "__system__" && onEdit;
  const startEdit = () => { setDraft(msg.text || ""); setEditing(true); };
  const saveEdit = async () => {
    const ok = await onEdit(msg, draft);
    if (ok) setEditing(false);
  };
  const reactionEntries = Object.entries(msg.reactions || {}).filter(([, ids]) => (ids || []).length > 0);
  const reactorNames = (ids) =>
    (ids || []).map((uid) => (uid === currentUserId ? "You" : (userNameById?.[uid] || "Someone"))).join(", ");
  return (
    <div id={`msg-${msg.id}`} className={`flex ${mine ? "justify-end" : "justify-start"} group`} data-testid={`chat-message-${msg.id}`}>
      <div className={`max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
        {/* Hover action row */}
        <div className={`flex items-center gap-0.5 mb-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-slate-200 rounded-xl shadow-sm px-1 py-0.5 ${mine ? "justify-end" : "justify-start"}`}>
          {onReact && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setPickerOpen((v) => !v)}
                title="Add reaction"
                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-[#F54800] transition-colors"
                data-testid={`react-btn-${msg.id}`}
              >
                <SmilePlus size={16} />
              </button>
              {pickerOpen && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setPickerOpen(false)} />
                  <div
                    className={`absolute z-30 top-full mt-1 ${mine ? "right-0" : "left-0"} flex items-center gap-1 bg-white border border-slate-200 shadow-xl rounded-2xl px-2 py-1.5`}
                    data-testid={`reaction-picker-${msg.id}`}
                  >
                    {REACTION_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => { onReact(msg, emoji); setPickerOpen(false); }}
                        className="w-9 h-9 flex items-center justify-center rounded-xl text-lg hover:bg-slate-100 transition-colors"
                        data-testid={`reaction-pick-${msg.id}-${emoji}`}
                      >
                        {emoji}
                      </button>
                    ))}
                    <button type="button" onClick={() => { setFullEmojiOpen(true); setPickerOpen(false); }} title="More emoji" className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 transition-colors" data-testid={`reaction-more-${msg.id}`}><Plus size={16} /></button>
                  </div>
                </>
              )}
              {fullEmojiOpen && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setFullEmojiOpen(false)} />
                  <div className={`absolute z-30 top-full mt-1 ${mine ? "right-0" : "left-0"}`} data-testid={`reaction-emoji-picker-${msg.id}`}>
                    <EmojiPicker onPick={(emoji) => { onReact(msg, emoji); setFullEmojiOpen(false); }} />
                  </div>
                </>
              )}
            </div>
          )}
          <button type="button" onClick={() => onReply(msg)} title="Quote reply" className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors" data-testid={`reply-btn-${msg.id}`}><CornerUpLeft size={16} /></button>
          {onCopyLink && (
            <button type="button" onClick={() => onCopyLink(msg)} title="Copy link to message" className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-[#F54800] transition-colors" data-testid={`copy-link-btn-${msg.id}`}><LinkIcon size={16} /></button>
          )}
          {onConvertTask && !msg.is_system && msg.sender_id !== "__system__" && (
            <button type="button" onClick={() => onConvertTask(msg)} title="Convert to task" className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-[#F54800] transition-colors" data-testid={`task-btn-${msg.id}`}><CheckSquare size={16} /></button>
          )}
          {editable && (
            <button type="button" onClick={startEdit} title="Edit" className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors" data-testid={`edit-btn-${msg.id}`}><Pencil size={16} /></button>
          )}
          <button type="button" onClick={() => onPin(msg)} title={msg.pinned ? "Unpin" : "Pin"} className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${msg.pinned ? "text-[#F54800]" : "text-slate-500 hover:text-[#F54800]"}`} data-testid={`pin-btn-${msg.id}`}><Pin size={16} fill={msg.pinned ? "currentColor" : "none"} /></button>
          {onSave && (
            <button type="button" onClick={() => onSave(msg)} title={saved ? "Remove from Saved" : "Save for later"} className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${saved ? "text-[#F54800]" : "text-slate-500 hover:text-[#F54800]"}`} data-testid={`save-btn-${msg.id}`}>
              {saved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
            </button>
          )}
          {canShare && onPushUpdate && (
            <button
              type="button"
              onClick={() => onPushUpdate(msg)}
              title={msg.client_shared ? "Retract from Project Update" : "Push to Project Update"}
              className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${msg.client_shared ? "text-emerald-600" : "text-slate-500 hover:text-emerald-600"}`}
              data-testid={`push-update-${msg.id}`}
            >
              {msg.client_shared ? <Eye size={16} /> : <Send size={16} />}
            </button>
          )}
          {canShare && !onPushUpdate && onShareToggle && (
            <button
              type="button"
              onClick={() => onShareToggle(msg)}
              title={msg.client_shared ? "Retract from client" : "Share with client"}
              className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${msg.client_shared ? "text-emerald-600" : "text-slate-500 hover:text-emerald-600"}`}
              data-testid={`share-toggle-${msg.id}`}
            >
              {msg.client_shared ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
          )}
        </div>


        <div
          className={`px-3.5 py-2.5 text-sm shadow-sm ${
            mine
              ? "bg-slate-900 text-white rounded-2xl rounded-br-md"
              : `border ${taggedMe ? "border-[#F54800]/60 bg-[#F54800]/5" : "border-slate-200 bg-white"} text-slate-900 rounded-2xl rounded-bl-md`
          }`}
        >
          {!mine && (
            <div className="flex items-center gap-1.5 mb-1">
              <MiniAvatar src={resolveAvatar(userAvatarById?.[msg.sender_id])} name={msg.sender_name} size={20} userId={msg.sender_id} />
              <span className="text-xs font-semibold text-slate-600">{msg.sender_name}</span>
            </div>
          )}
          {quoted && (
            <button
              type="button"
              onClick={() => document.getElementById(`msg-${quoted.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
              className={`block w-full text-left border-l-2 pl-2 mb-1.5 text-xs truncate ${mine ? "border-white/30 text-white/60" : "border-slate-300 text-slate-500"}`}
              data-testid={`quoted-ref-${msg.id}`}
            >
              {quoted.sender_name}: {quoted.text || "📎 attachment"}
            </button>
          )}
          {msg.annotation_of && (
            <div className={`text-[10px] mb-1 inline-flex items-center gap-1 ${mine ? "text-white/50" : "text-slate-400"}`}><Pencil size={9} /> Annotated feedback</div>
          )}
          {editing ? (
            <div className="min-w-[200px]" data-testid={`edit-editor-${msg.id}`}>
              <textarea
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                  if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
                }}
                rows={2}
                className={`w-full rounded-sm text-sm px-2 py-1.5 resize-none ${mine ? "bg-white/10 text-white placeholder-white/40 border border-white/20" : "bg-white text-slate-900 border border-slate-300"}`}
                data-testid={`edit-input-${msg.id}`}
              />
              <div className="flex items-center gap-2 mt-1">
                <button type="button" onClick={saveEdit} className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm ${mine ? "bg-white text-slate-900" : "bg-slate-900 text-white"}`} data-testid={`edit-save-${msg.id}`}>Save</button>
                <button type="button" onClick={() => setEditing(false)} className={`text-[11px] px-2 py-0.5 rounded-sm ${mine ? "text-white/70 hover:text-white" : "text-slate-500 hover:text-slate-800"}`} data-testid={`edit-cancel-${msg.id}`}>Cancel</button>
                <span className={`text-[10px] ${mine ? "text-white/40" : "text-slate-400"}`}>Enter to save · Esc to cancel</span>
              </div>
            </div>
          ) : (
            <>
              {msg.text && (
                <div className="chat-body break-words">
                  {renderRich(msg.text, msg.mentions, currentUserId)}
                  {msg.edited_at && (
                    <span className={`ml-1 align-baseline text-[10px] ${mine ? "text-white/60" : "text-slate-400"}`} title={`Edited ${msg.edited_at}`} data-testid={`edited-marker-${msg.id}`}>(edited)</span>
                  )}
                </div>
              )}
              {atts.map((a, i) => (
                <AttachmentView key={i} att={a} onOpen={onOpenImage} onAnnotate={a?.type === "image" ? () => onAnnotate(msg, a) : null} />
              ))}
              {!msg.text && msg.edited_at && (
                <span className={`ml-1 align-baseline text-[10px] ${mine ? "text-white/60" : "text-slate-400"}`} title={`Edited ${msg.edited_at}`} data-testid={`edited-marker-${msg.id}`}>(edited)</span>
              )}
            </>
          )}
        </div>

        {msg.client_shared && (
          <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600" data-testid={`client-visible-badge-${msg.id}`}>
            <Eye size={9} /> Client Visible
          </span>
        )}

        {msg.created_at && (
          <RelativeTime date={msg.created_at} className={`mt-0.5 text-[10px] text-slate-400 ${mine ? "text-right" : "text-left"}`} testid={`msg-time-${msg.id}`} />
        )}

        {reactionEntries.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`reactions-${msg.id}`}>
            {reactionEntries.map(([emoji, ids]) => {
              const reacted = (ids || []).includes(currentUserId);
              return (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => onReact && onReact(msg, emoji)}
                  title={reactorNames(ids)}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs leading-none transition-all active:scale-95 ${
                    reacted
                      ? "bg-[#F54800]/10 border-[#F54800]/40 text-[#B7350A] font-semibold"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`reaction-pill-${msg.id}-${emoji}`}
                >
                  <span>{emoji}</span>
                  <span data-testid={`reaction-count-${msg.id}-${emoji}`}>{ids.length}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Chat() {
  const { user } = useAuth();
  const can = usePermissions();
  const { channels: unreadCh, dms: unreadDm, markRead, refresh: refreshUnread } = useUnread();
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState(null); // {type: 'channel'|'dm', id}
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  // @-mention autocomplete state — when the user types `@<query>` we surface a
  // floating dropdown of users; picking one inserts `@FullName ` and records
  // the user_id in `mentions` so the backend can notify them in-app.
  // Reset between sends.
  const [mentions, setMentions] = useState([]); // [{id, name}]
  const [mentionQuery, setMentionQuery] = useState(null); // null = closed, "" or "foo" = open
  const [mentionIndex, setMentionIndex] = useState(0);
  // :shortcode: emoji autocomplete state (mirrors the mention dropdown).
  const [shortcodeQuery, setShortcodeQuery] = useState(null); // null = closed
  const [shortcodeIndex, setShortcodeIndex] = useState(0);
  const richRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [newCh, setNewCh] = useState({ name: "", description: "", is_private: false, member_ids: [], folder: "" });
  const [membersOpen, setMembersOpen] = useState(false);
  const [editMembers, setEditMembers] = useState([]);
  const [editPrivate, setEditPrivate] = useState(false);
  const [editFolder, setEditFolder] = useState("");
  const [collapsedFolders, setCollapsedFolders] = useState({});
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameVal, setRenameVal] = useState("");
  const listRef = useRef(null);
  const activeRef = useRef(null);
  useEffect(() => { activeRef.current = active; }, [active]);

  // Typing indicators — ephemeral, relayed over the same WebSocket. We keep a
  // live WS handle (wsRef), a throttle timestamp, and a map of who's currently
  // typing in the OPEN conversation (id -> name) with per-user auto-clear timers.
  const wsRef = useRef(null);
  const wsConnectedRef = useRef(false);
  const lastTypingRef = useRef(0);
  const typingTimers = useRef({});
  const [typingUsers, setTypingUsers] = useState({}); // { userId: name }

  const sendTypingSignal = useCallback((stop) => {
    const ws = wsRef.current;
    const cur = activeRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !cur) return;
    const now = Date.now();
    if (stop) {
      lastTypingRef.current = 0; // let the next keystroke re-emit immediately
    } else {
      if (now - lastTypingRef.current < 2500) return; // throttle ~2.5s
      lastTypingRef.current = now;
    }
    const base = cur.type === "channel" ? { channel_id: cur.id } : { recipient_id: cur.id };
    try { ws.send(JSON.stringify({ type: "typing", stop: !!stop, ...base })); }
    catch (err) { logger.debug("typing send failed:", err?.message || err); }
  }, []);

  // Phase 148 — Studio Creative Hub: attachments, quote-reply, pin, share-to-client, archive.
  const [attachments, setAttachments] = useState([]); // [{url, name, type, size}]
  const [lightboxImage, setLightboxImage] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // message being quoted
  const [annotateTarget, setAnnotateTarget] = useState(null); // {msg, att}
  const [showArchived, setShowArchived] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pendingHighlight, setPendingHighlight] = useState(null); // message id to jump to after load
  const [olderLoaded, setOlderLoaded] = useState(false); // archived (>12mo) history loaded for this conversation
  // Mute (per-conversation, server-backed) + unread divider cursor.
  const [mutedChannels, setMutedChannels] = useState(() => new Set());
  const [mutedDms, setMutedDms] = useState(() => new Set());
  // Starred (per-conversation, server-backed) — pinned to a Starred section at the top.
  const [starredChannels, setStarredChannels] = useState(() => new Set());
  const [starredDms, setStarredDms] = useState(() => new Set());
  const [readCursor, setReadCursor] = useState(null); // last_read_at captured when the conversation opened
  const [peerSeenAt, setPeerSeenAt] = useState(null); // DM read-receipt: how far the peer has read
  const [savedIds, setSavedIds] = useState(() => new Set()); // message ids this user bookmarked
  const [savedList, setSavedList] = useState([]); // full saved-message rows from GET /chat/saved
  const [savedOpen, setSavedOpen] = useState(false); // Saved side panel toggle
  const [mentionList, setMentionList] = useState([]); // recent messages that @-mention me
  const [mentionOpen, setMentionOpen] = useState(false); // Mentions inbox toggle
  const [mentionSeenAt, setMentionSeenAt] = useState(() => localStorage.getItem("chat_mentions_seen_at") || ""); // last time I opened the inbox
  const fileInputRef = useRef(null);

  // Sidebar width (desktop only) — persisted to localStorage. Min 220, max 480.
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = Number(typeof window !== "undefined" ? window.localStorage.getItem("chat-sidebar-w") : 0);
    if (stored >= 220 && stored <= 480) return stored;
    return 288;
  });
  const dragging = useRef(false);
  const onDragStart = (e) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      const x = e.touches?.[0]?.clientX ?? e.clientX;
      // The sidebar is anchored after the app sidebar in Layout. We just clamp based on
      // distance from the sidebar's left edge (it's positioned right after the global nav).
      const sidebar = document.querySelector('[data-testid="chat-sidebar"]');
      if (!sidebar) return;
      const rect = sidebar.getBoundingClientRect();
      const w = Math.min(480, Math.max(220, x - rect.left));
      setSidebarWidth(w);
    };
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try { window.localStorage.setItem("chat-sidebar-w", String(sidebarWidth)); } catch (err) { logger.debug("localStorage write failed:", err?.message || err); }
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [sidebarWidth]);

  // Swipe-right-to-back gesture on the thread pane (mobile only)
  const touchStart = useRef(null);
  const onThreadTouchStart = (e) => {
    if (window.matchMedia("(min-width: 1024px)").matches) return; // desktop ignores
    const t = e.touches?.[0];
    if (!t) return;
    // Only register if the gesture begins within the leftmost 36px (edge swipe)
    if (t.clientX > 36) { touchStart.current = null; return; }
    touchStart.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };
  const onThreadTouchEnd = (e) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = Math.abs(t.clientY - start.y);
    const dt = Date.now() - start.time;
    // Horizontal swipe ≥ 60px, mostly horizontal (dx > 2*dy), within 600ms
    if (dx >= 60 && dx > dy * 2 && dt < 600) {
      setActive(null);
    }
  };

  // Guarded conversation selector — re-selecting the already-open conversation
  // is a no-op (keeps the same object ref) so the unread divider/read-state
  // effect doesn't re-run and wipe the "new messages" line.
  const selectConversation = useCallback((type, id) => {
    setActive((cur) => (cur && cur.type === type && cur.id === id) ? cur : { type, id });
  }, []);

  // On opening a conversation: capture the last-read timestamp FIRST (for the
  // "new messages" divider), then mark it read.
  useEffect(() => {
    if (!active) { setReadCursor(null); return undefined; }
    let cancelled = false;
    setReadCursor(null);
    api.get("/chat/read-state", { params: { scope_type: active.type, scope_id: active.id } })
      .then(({ data }) => { if (!cancelled) setReadCursor(data?.last_read_at || null); })
      .catch(() => { if (!cancelled) setReadCursor(null); })
      .finally(() => { markRead(active.type, active.id); });
    return () => { cancelled = true; };
  }, [active, markRead]);

  // DM read-receipt — poll how far the peer has read so we can show "Seen"
  // under our last outgoing message. DMs only (channels have no 1:1 receipt).
  useEffect(() => {
    if (!active || active.type !== "dm") { setPeerSeenAt(null); return undefined; }
    let alive = true;
    const fetchSeen = () => api.get("/chat/dm-seen", { params: { peer_id: active.id } })
      .then(({ data }) => { if (alive) setPeerSeenAt(data?.last_read_at || null); })
      .catch(() => {});
    fetchSeen();
    const iv = setInterval(fetchSeen, 12000);
    return () => { alive = false; clearInterval(iv); };
  }, [active]);

  // Per-user conversation mutes.
  const loadMutes = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/mutes");
      setMutedChannels(new Set(data?.channels || []));
      setMutedDms(new Set(data?.dms || []));
    } catch (err) { logger.debug("mutes fetch failed:", err?.message || err); }
  }, []);
  useEffect(() => { loadMutes(); }, [loadMutes]);

  // Per-user conversation stars (Starred section).
  const loadStars = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/stars");
      setStarredChannels(new Set(data?.channels || []));
      setStarredDms(new Set(data?.dms || []));
    } catch (err) { logger.debug("stars fetch failed:", err?.message || err); }
  }, []);
  useEffect(() => { loadStars(); }, [loadStars]);

  // When the open conversation changes, clear any locally-shown typing lines
  // and, on leaving, tell the people in the conversation we left that we
  // stopped typing.
  useEffect(() => {
    setTypingUsers({});
    Object.values(typingTimers.current).forEach(clearTimeout);
    typingTimers.current = {};
    if (!active) return undefined;
    return () => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        const base = active.type === "channel" ? { channel_id: active.id } : { recipient_id: active.id };
        try { ws.send(JSON.stringify({ type: "typing", stop: true, ...base })); }
        catch (err) { logger.debug("typing stop failed:", err?.message || err); }
      }
    };
  }, [active]);

  const loadChannels = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/channels");
      setChannels(data);
      // Auto-select first non-archived channel only on desktop.
      const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
      if (isDesktop) {
        const firstActive = data.find((c) => !c.archived);
        setActive((cur) => cur || (firstActive ? { type: "channel", id: firstActive.id } : null));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users", { params: { kind: "chat_members" } });
      setUsers(data.filter((u) => u.id !== user.id));
    } catch (err) {
      logger.warn("Users directory fetch failed:", err?.message || err);
    }
  }, [user.id]);

  // Direct-message conversations — only people actually messaged, newest first.
  const [dmThreads, setDmThreads] = useState([]);
  const [dmPickerOpen, setDmPickerOpen] = useState(false);
  const [dmPickerQuery, setDmPickerQuery] = useState("");
  const loadDmThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/dm-threads");
      setDmThreads(Array.isArray(data) ? data : []);
    } catch (err) {
      logger.warn("DM threads fetch failed:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    loadChannels();
    loadUsers();
    loadDmThreads();
  }, [loadChannels, loadUsers, loadDmThreads]);

  // Load messages whenever active conversation changes
  useEffect(() => {
    if (!active) return;
    const params = active.type === "channel" ? { channel_id: active.id } : { peer_id: active.id };
    api.get("/chat/messages", { params })
      .then(({ data }) => {
        setMessages(data);
        setOlderLoaded(false);
        setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
      })
      .catch((err) => {
        toast.error(formatApiError(err));
      });
  }, [active]);

  // Day 2 — debounced message search across visible channels + DMs.
  useEffect(() => {
    if (!searchOpen) return;
    const term = searchQ.trim();
    if (term.length < 2) { setSearchResults([]); setSearchLoading(false); return; }
    setSearchLoading(true);
    const t = setTimeout(() => {
      api.get("/chat/search", { params: { q: term } })
        .then(({ data }) => setSearchResults(data || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ, searchOpen]);

  // After jumping to a conversation from a search hit, scroll to + flash it
  // once that conversation's messages have loaded.
  useEffect(() => {
    if (!pendingHighlight) return;
    const el = document.getElementById(`msg-${pendingHighlight}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-[#F54800]", "rounded-sm");
    const tm = setTimeout(() => {
      el.classList.remove("ring-2", "ring-[#F54800]", "rounded-sm");
      setPendingHighlight(null);
    }, 2200);
    return () => clearTimeout(tm);
  }, [messages, pendingHighlight]);

  const openSearchResult = (r) => {
    setActive(r.is_dm ? { type: "dm", id: r.peer_id } : { type: "channel", id: r.channel_id });
    setPendingHighlight(r.id);
    setSearchOpen(false);
    setSearchQ("");
    setSearchResults([]);
  };

  // Retention — load the archived (>12-month-old) history for this conversation on demand.
  const loadOlderMessages = () => {
    if (!active) return;
    const params = active.type === "channel"
      ? { channel_id: active.id, include_archived: true }
      : { peer_id: active.id, include_archived: true };
    api.get("/chat/messages", { params })
      .then(({ data }) => { setMessages(data); setOlderLoaded(true); })
      .catch((err) => toast.error(formatApiError(err)));
  };

  // Real-time delivery — resilient WebSocket: auto-reconnect with backoff +
  // a periodic keepalive ping so proxy idle-timeouts don't silently kill the
  // live feed. A polling fallback (below) covers environments where the
  // socket can't stay up at all, so new messages always appear in the open
  // conversation without the user switching channels.
  useEffect(() => {
    let ws = null;
    let reconnectTimer = null;
    let keepalive = null;
    let attempts = 0;
    let unmounted = false;

    const scheduleReconnect = () => {
      if (unmounted) return;
      attempts += 1;
      const delay = Math.min(1000 * Math.pow(1.6, attempts), 15000);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, delay);
    };

    const handleMessage = (ev) => {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch (err) {
        logger.debug("WS message parse failed:", err?.message || err);
        return;
      }
      const cur = activeRef.current;
      if (!cur) return;

      // Ephemeral typing signal — not a message. Only reflect it when it
      // belongs to the open conversation and isn't our own.
      if (msg.type === "typing") {
        if (msg.sender_id === user.id) return;
        const forChannel = cur.type === "channel" && msg.channel_id === cur.id;
        const forDM = cur.type === "dm" && msg.sender_id === cur.id && msg.recipient_id === user.id;
        if (!forChannel && !forDM) return;
        const sid = msg.sender_id;
        if (typingTimers.current[sid]) clearTimeout(typingTimers.current[sid]);
        if (msg.stop) {
          delete typingTimers.current[sid];
          setTypingUsers((t) => { const n = { ...t }; delete n[sid]; return n; });
        } else {
          setTypingUsers((t) => ({ ...t, [sid]: msg.sender_name || "Someone" }));
          typingTimers.current[sid] = setTimeout(() => {
            delete typingTimers.current[sid];
            setTypingUsers((t) => { const n = { ...t }; delete n[sid]; return n; });
          }, 4000); // auto-clear if no keystroke for ~4s
        }
        return;
      }

      const matchChannel = cur.type === "channel" && msg.channel_id === cur.id;
      const matchDM =
        cur.type === "dm" &&
        ((msg.sender_id === user.id && msg.recipient_id === cur.id) ||
          (msg.sender_id === cur.id && msg.recipient_id === user.id));
      if (matchChannel || matchDM) {
        // A real message from this sender clears their typing line instantly.
        if (typingTimers.current[msg.sender_id]) { clearTimeout(typingTimers.current[msg.sender_id]); delete typingTimers.current[msg.sender_id]; }
        setTypingUsers((t) => {
          if (!(msg.sender_id in t)) return t;
          const n = { ...t }; delete n[msg.sender_id]; return n;
        });
        // Phase 148 — pin/share toggles re-broadcast the SAME message id
        // (an update, not a new message) — patch in place instead of
        // appending a duplicate bubble.
        setMessages((m) => {
          const idx = m.findIndex((x) => x.id === msg.id);
          if (idx >= 0) { const next = [...m]; next[idx] = msg; return next; }
          // Only a genuinely new message scrolls to the bottom — in-place
          // updates (reactions, pin, share) must not yank viewers down.
          setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
          return [...m, msg];
        });
        // Auto-mark-read since user is viewing
        let scopeType = "dm";
        let scopeId;
        if (matchChannel) {
          scopeType = "channel";
          scopeId = msg.channel_id;
        } else if (msg.sender_id === user.id) {
          scopeId = msg.recipient_id;
        } else {
          scopeId = msg.sender_id;
        }
        markRead(scopeType, scopeId);
      } else {
        // Refresh unread counts in background
        refreshUnread();
      }
      // Toast the current user whenever they're @-mentioned in a fresh message
      // they didn't send — even when the channel/DM isn't open.
      if (msg.sender_id !== user.id && (msg.mentions || []).includes(user.id)) {
        toast(`💬 ${msg.sender_name} mentioned you`, {
          description: (msg.text || "").slice(0, 100),
          duration: 6000,
        });
        loadMentions(); // keep the Mentions inbox fresh
      }
    };

    function connect() {
      if (unmounted) return;
      try { ws = new WebSocket(getWsUrl()); }
      catch (err) { logger.debug("WS open failed:", err?.message || err); scheduleReconnect(); return; }
      wsRef.current = ws;
      ws.onopen = () => {
        attempts = 0;
        wsConnectedRef.current = true;
        if (keepalive) clearInterval(keepalive);
        keepalive = setInterval(() => {
          try { if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" })); }
          catch (e) { /* ignore keepalive errors */ }
        }, 25000);
      };
      ws.onmessage = handleMessage;
      ws.onerror = (err) => logger.warn("WebSocket error:", err);
      ws.onclose = () => {
        wsConnectedRef.current = false;
        if (keepalive) { clearInterval(keepalive); keepalive = null; }
        scheduleReconnect();
      };
    }

    connect();

    return () => {
      unmounted = true;
      wsConnectedRef.current = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (keepalive) clearInterval(keepalive);
      try { if (ws) ws.close(); } catch (err) {
        logger.debug("WS close failed:", err);
      }
    };
  }, [user.id, markRead, refreshUnread]);

  // Polling fallback — guarantees new messages reach the OPEN conversation
  // even where the WebSocket can't stay connected (restrictive proxies, idle
  // timeouts). Runs a light merge-sync only while the socket is down and the
  // tab is visible; stays completely idle when the WebSocket is healthy.
  useEffect(() => {
    if (!active) return;
    const tick = async () => {
      if (wsConnectedRef.current) return;
      if (typeof document !== "undefined" && document.hidden) return;
      const cur = activeRef.current;
      if (!cur) return;
      const params = cur.type === "channel" ? { channel_id: cur.id } : { peer_id: cur.id };
      try {
        const { data } = await api.get("/chat/messages", { params });
        if (!Array.isArray(data)) return;
        setMessages((prev) => {
          const byId = new Map(prev.map((m) => [m.id, m]));
          let changed = false;
          for (const m of data) {
            const old = byId.get(m.id);
            if (!old) { byId.set(m.id, m); changed = true; }
            else if (old.edited_at !== m.edited_at || old.pinned !== m.pinned || old.client_shared !== m.client_shared || JSON.stringify(old.reactions) !== JSON.stringify(m.reactions)) {
              byId.set(m.id, m); changed = true;
            }
          }
          if (!changed) return prev;
          const merged = Array.from(byId.values()).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          if (merged.length > prev.length) setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
          return merged;
        });
      } catch (err) { /* silent — this is the fallback path */ }
    };
    const id = setInterval(tick, 3500);
    return () => clearInterval(id);
  }, [active]);

  // Insert the picked @mention at the CARET (works mid-text / at the start —
  // not just the end). Replaces the "@<query>" immediately before the cursor
  // with "@FullName " and restores the caret just after it.
  const insertMention = (u) => {
    const el = inputRef.current;
    const full = text;
    const caret = el && typeof el.selectionStart === "number" ? el.selectionStart : full.length;
    const before = full.slice(0, caret);
    const after = full.slice(caret);
    const m = before.match(/(^|\s)@(\w*)$/);
    if (!m) { setMentionQuery(null); return; }
    const start = before.length - m[2].length - 1; // index of the '@'
    const insert = `@${u.name} `;
    const next = full.slice(0, start) + insert + after;
    setText(next);
    setMentions((prev) => prev.find((p) => p.id === u.id) ? prev : [...prev, { id: u.id, name: u.name }]);
    setMentionQuery(null);
    setMentionIndex(0);
    const newCaret = start + insert.length;
    setTimeout(() => {
      const node = inputRef.current;
      if (node) { node.focus(); try { node.setSelectionRange(newCaret, newCaret); } catch (e) { /* noop */ } }
    }, 0);
  };
  const [composerEmojiOpen, setComposerEmojiOpen] = useState(false);
  // Insert any emoji at the caret in the composer (Slack-style).
  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    const full = text;
    const caret = el && typeof el.selectionStart === "number" ? el.selectionStart : full.length;
    const next = full.slice(0, caret) + emoji + full.slice(caret);
    setText(next);
    const newCaret = caret + emoji.length;
    setTimeout(() => { const n = inputRef.current; if (n) { n.focus(); try { n.setSelectionRange(newCaret, newCaret); } catch (e) { /* noop */ } } }, 0);
  };
  const shortcodeItems = useMemo(() => searchShortcodes(shortcodeQuery || "", 8), [shortcodeQuery]);
  // Insert the chosen :shortcode: emoji at the caret (replaces the ":query").
  const insertShortcode = (e) => {
    const el = inputRef.current;
    const caret = el && typeof el.selectionStart === "number" ? el.selectionStart : text.length;
    const { next, caret: newCaret } = replaceShortcode(text, caret, e.emoji);
    setText(next);
    setShortcodeQuery(null);
    setShortcodeIndex(0);
    setTimeout(() => { const n = inputRef.current; if (n) { n.focus(); try { n.setSelectionRange(newCaret, newCaret); } catch (err) { /* noop */ } } }, 0);
  };
  const send = async () => {
    const md = richRef.current ? richRef.current.getMarkdown() : "";
    const mentionIds = richRef.current ? richRef.current.getMentionIds() : [];
    if ((!md.trim() && attachments.length === 0) || !active) return;
    const base = {
      text: md,
      mentions: mentionIds,
      attachments: attachments.length ? attachments : undefined,
      reply_to_id: replyTo?.id || undefined,
    };
    const payload = active.type === "channel" ? { channel_id: active.id, ...base } : { recipient_id: active.id, ...base };
    try {
      const { data: created } = await api.post("/chat/messages", payload);
      if (!wsConnectedRef.current && created && created.id) {
        setMessages((m) => (m.some((x) => x.id === created.id) ? m : [...m, created]));
        setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
      }
      richRef.current?.clear();
      setAttachments([]);
      setReplyTo(null);
      sendTypingSignal(true); // message sent → stop typing
      if (active.type === "dm") loadDmThreads(); // surface/re-sort the DM thread
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Phase 148 — attach a PDF/high-res image to the NEXT message sent.
  const pickFile = () => fileInputRef.current?.click();
  const ALLOWED_EXT = ["jpg", "jpeg", "png", "avif", "gif", "webp", "pdf", "stp", "step", "sldprt", "sldasm", "f3d", "stl", "igs", "iges", "obj", "3mf", "dwg", "dxf", "zip", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv", "txt"];
  // Upload one or many files (from the picker or a drag-drop). Each valid file
  // is uploaded and appended to the attachments list so a single message can
  // carry several images/files.
  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 100 * 1024 * 1024) { toast.error(`${file.name}: max file size is 100 MB`); continue; }
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        const isImage = file.type.startsWith("image/");
        if (!isImage && !ALLOWED_EXT.includes(ext)) { toast.error(`${file.name}: that file type can't be attached`); continue; }
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setAttachments((prev) => [...prev, { url: data.path, name: file.name, type: isImage ? "image" : "file", size: data.size }]);
        } catch (err) { toast.error(formatApiError(err)); }
      }
    } finally { setUploading(false); }
  };
  const handleFileSelect = async (e) => {
    // Copy the live FileList BEFORE clearing the input — resetting value
    // empties the same FileList object in Chromium.
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    await uploadFiles(list);
  };

  // Annotation — "Save & Share" hands back a new attachment; post it as a
  // fresh reply in the same thread, referencing the original message.
  const handleAnnotationSaved = async (att) => {
    if (!active || active.type !== "channel" || !annotateTarget) return;
    try {
      await api.post("/chat/messages", {
        channel_id: active.id,
        text: "📐 Annotated feedback",
        attachments: [att],
        annotation_of: annotateTarget.msg.id,
      });
    } catch (err) { toast.error(formatApiError(err)); }
    setAnnotateTarget(null);
  };

  const pinMessage = async (msg) => {
    try { await api.post(`/chat/messages/${msg.id}/pin`); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const shareToggle = async (msg) => {
    try {
      await api.post(`/chat/messages/${msg.id}/share-to-client`);
      toast.success(msg.client_shared ? "Retracted from client view" : "Shared to client — now visible in their Project Updates");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reactToMessage = async (msg, emoji) => {
    const uid = user.id;
    const toggle = (m) => {
      if (m.id !== msg.id) return m;
      const rx = { ...(m.reactions || {}) };
      const had = (rx[emoji] || []).includes(uid);
      let who = (rx[emoji] || []).filter((x) => x !== uid);
      if (!had) who = [...who, uid];
      if (who.length) rx[emoji] = who; else delete rx[emoji];
      return { ...m, reactions: rx };
    };
    // Optimistic UI: flip the pill instantly; the server confirms right after.
    setMessages((list) => list.map(toggle));
    try { await api.post(`/chat/messages/${msg.id}/react`, { emoji }); }
    catch (err) {
      setMessages((list) => list.map(toggle)); // revert on failure
      toast.error(formatApiError(err));
    }
  };

  const editMessage = async (msg, newText) => {
    const t = (newText || "").trim();
    if (!t && !(msg.attachments || []).length) { toast.error("Message can't be empty"); return false; }
    if (t === (msg.text || "")) return true; // no change — just close the editor
    try { await api.patch(`/chat/messages/${msg.id}`, { text: t }); return true; }
    catch (err) { toast.error(formatApiError(err)); return false; }
  };

  const createChannel = async () => {
    if (!newCh.name.trim()) { toast.error("Channel name is required"); return; }
    try {
      await api.post("/chat/channels", {
        name: newCh.name.trim(),
        description: newCh.description,
        is_private: newCh.is_private,
        member_ids: newCh.is_private ? newCh.member_ids : [],
        folder: (newCh.folder || "").trim() || undefined,
      });
      setOpen(false);
      setNewCh({ name: "", description: "", is_private: false, member_ids: [], folder: "" });
      loadChannels();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  // Phase 5 — Convert a chat message into a task (daily-task).
  const [taskMsg, setTaskMsg] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", due_date: "", project_id: "", assignee_id: "" });
  const [taskProjects, setTaskProjects] = useState([]);
  const [taskSaving, setTaskSaving] = useState(false);
  const openTaskDialog = async (msg) => {
    const raw = (msg.text || "").trim();
    const firstLine = (raw.split("\n")[0] || "").slice(0, 120);
    const today = new Date().toISOString().slice(0, 10);
    setTaskForm({ title: firstLine || "Task from chat", description: raw, due_date: today, project_id: "", assignee_id: "" });
    setTaskMsg(msg);
    try { const { data } = await api.get("/daily-tasks/projects"); setTaskProjects(Array.isArray(data) ? data : []); }
    catch (err) { setTaskProjects([]); }
  };
  const saveTask = async () => {
    const title = (taskForm.title || "").trim();
    if (!title) { toast.error("Task title is required"); return; }
    if (!taskForm.due_date) { toast.error("Please pick a due date"); return; }
    setTaskSaving(true);
    try {
      await api.post("/daily-tasks", {
        title,
        description: (taskForm.description || "").trim() || undefined,
        due_date: taskForm.due_date,
        project_id: taskForm.project_id || undefined,
        assignee_id: taskForm.assignee_id || undefined,
      });
      toast.success("Task created from message");
      setTaskMsg(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTaskSaving(false); }
  };
  const openMembers = () => { setEditMembers(activeMeta?.member_ids || []); setEditPrivate(!!activeMeta?.is_private); setEditFolder(activeMeta?.folder || ""); setMembersOpen(true); };
  const saveMembers = async () => {
    if (!active || active.type !== "channel") return;
    try {
      await api.patch(`/chat/channels/${active.id}`, { member_ids: editMembers, is_private: editPrivate, folder: (editFolder || "").trim() });
      toast.success("Group updated");
      setMembersOpen(false);
      loadChannels();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const renameChannel = () => { setRenameVal(activeMeta?.name || ""); setRenameOpen(true); };
  const saveRename = async () => {
    const nm = (renameVal || "").trim();
    if (!active || active.type !== "channel" || !nm || nm === activeMeta?.name) { setRenameOpen(false); return; }
    try {
      await api.patch(`/chat/channels/${active.id}`, { name: nm });
      toast.success("Channel renamed");
      setRenameOpen(false);
      loadChannels();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // In-app delete confirmation (replaces window.confirm so it renders
  // consistently inside the app, including on mobile browsers).
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const deleteChannel = () => {
    if (!active || active.type !== "channel") return;
    if (user.role !== "admin" && user.role !== "founder") { toast.error("Only admins can delete channels"); return; }
    setDeleteOpen(true);
  };
  const confirmDeleteChannel = async () => {
    if (!active || active.type !== "channel") return;
    setDeleteBusy(true);
    try {
      await api.delete(`/chat/channels/${active.id}`);
      toast.success("Channel deleted");
      setDeleteOpen(false);
      setActive(null); setMessages([]);
      loadChannels();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDeleteBusy(false); }
  };

  // Phase 148 — Channel Archiving: hidden from the default list, history
  // stays readable via the "Archived" filter, read-only (no composer).
  const toggleArchive = async () => {
    if (!active || active.type !== "channel") return;
    const archiving = !activeMeta?.archived;
    try {
      await api.patch(`/chat/channels/${active.id}`, { archived: archiving });
      toast.success(archiving ? `#${activeMeta?.name} archived` : `#${activeMeta?.name} restored`);
      loadChannels();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const exportChannel = async () => {
    if (!active || active.type !== "channel") return;
    try {
      const resp = await api.get(`/chat/channels/${active.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chat-${activeMeta?.name || "channel"}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("History downloaded");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const activeMeta = useMemo(() => {
    if (!active) return null;
    if (active.type === "channel") return channels.find((c) => c.id === active.id);
    return users.find((u) => u.id === active.id);
  }, [active, channels, users]);

  // Only project-linked channels have a client-visible Project Updates feed
  // to share into.
  const canShareToClient = active?.type === "channel" && !!activeMeta?.project_id;
  // Feb 2026 — Information Cleanse: #announcements is read-only for
  // everyone except Founders/Admins (mirrors the backend's literal role gate).
  const isAnnouncements = active?.type === "channel" && activeMeta?.name === "announcements";
  const canPostAnnouncements = user?.role === "founder" || user?.role === "admin";
  const composerLocked = isAnnouncements && !canPostAnnouncements;
  const messagesById = useMemo(() => Object.fromEntries(messages.map((m) => [m.id, m])), [messages]);
  const userNameById = useMemo(() => {
    const map = { [user.id]: user.name };
    (users || []).forEach((u) => { map[u.id] = u.name; });
    return map;
  }, [users, user.id, user.name]);
  const userAvatarById = useMemo(() => {
    const map = { [user.id]: user.avatar || "" };
    (users || []).forEach((u) => { map[u.id] = u.avatar || ""; });
    return map;
  }, [users, user.id, user.avatar]);
  const pinnedMessages = useMemo(() => messages.filter((m) => m.pinned), [messages]);
  const activeChannels = useMemo(() => channels.filter((c) => !c.archived), [channels]);
  const archivedChannels = useMemo(() => channels.filter((c) => c.archived), [channels]);

  // First unread message for the "new messages" divider (only when the user
  // has read this conversation before — readCursor set — and something newer
  // arrived from someone else).
  const firstUnreadId = useMemo(() => {
    if (!readCursor) return null;
    const hit = messages.find((m) => m.sender_id !== user.id && m.created_at && m.created_at > readCursor);
    return hit ? hit.id : null;
  }, [messages, readCursor, user.id]);
  const jumpToUnread = useCallback(() => {
    if (!firstUnreadId) return;
    document.getElementById(`msg-${firstUnreadId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [firstUnreadId]);

  // "Seen" marker — show only when the newest message is ours and the DM peer
  // has read up to (or past) it.
  const dmSeen = useMemo(() => {
    if (!active || active.type !== "dm" || !peerSeenAt || messages.length === 0) return false;
    const last = messages[messages.length - 1];
    return last.sender_id === user.id && last.created_at && peerSeenAt >= last.created_at;
  }, [active, peerSeenAt, messages, user.id]);

  // Mute controls for the open conversation.
  const isActiveMuted = !!active && (active.type === "channel" ? mutedChannels.has(active.id) : mutedDms.has(active.id));
  const toggleMute = useCallback(async () => {
    if (!active) return;
    const next = !isActiveMuted;
    const setter = active.type === "channel" ? setMutedChannels : setMutedDms;
    setter((prev) => { const n = new Set(prev); if (next) n.add(active.id); else n.delete(active.id); return n; });
    try {
      await api.post("/chat/mute", { scope_type: active.type, scope_id: active.id, muted: next });
      toast.success(next ? "Muted — you won't see unread badges here" : "Unmuted");
      refreshUnread();
    } catch (err) {
      // revert on failure
      setter((prev) => { const n = new Set(prev); if (next) n.delete(active.id); else n.add(active.id); return n; });
      toast.error(formatApiError(err));
    }
  }, [active, isActiveMuted, refreshUnread]);

  // Star controls for the open conversation.
  const isActiveStarred = !!active && (active.type === "channel" ? starredChannels.has(active.id) : starredDms.has(active.id));
  const toggleStar = useCallback(async () => {
    if (!active) return;
    const next = !isActiveStarred;
    const setter = active.type === "channel" ? setStarredChannels : setStarredDms;
    setter((prev) => { const n = new Set(prev); if (next) n.add(active.id); else n.delete(active.id); return n; });
    try {
      await api.post("/chat/star", { scope_type: active.type, scope_id: active.id, starred: next });
      toast.success(next ? "Starred" : "Removed from Starred");
    } catch (err) {
      setter((prev) => { const n = new Set(prev); if (next) n.delete(active.id); else n.add(active.id); return n; });
      toast.error(formatApiError(err));
    }
  }, [active, isActiveStarred]);

  // Copy a deep link to a specific message in the open conversation.
  const copyMessageLink = useCallback((msg) => {
    if (!active) return;
    const q = active.type === "channel" ? `channel=${active.id}` : `peer=${active.id}`;
    const link = `${window.location.origin}/chat?${q}&msg=${msg.id}`;
    const done = () => toast.success("Message link copied");
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(link).then(done).catch(() => {
          const ta = document.createElement("textarea"); ta.value = link; document.body.appendChild(ta);
          ta.select(); try { document.execCommand("copy"); done(); } catch (e) { toast.error("Copy failed"); }
          document.body.removeChild(ta);
        });
      } else {
        const ta = document.createElement("textarea"); ta.value = link; document.body.appendChild(ta);
        ta.select(); try { document.execCommand("copy"); done(); } catch (e) { toast.error("Copy failed"); }
        document.body.removeChild(ta);
      }
    } catch (err) { toast.error("Copy failed"); }
  }, [active]);

  // Save-for-later — personal per-user bookmarks on any message.
  const loadSaved = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/saved");
      setSavedList(data || []);
      setSavedIds(new Set((data || []).map((r) => r.message_id)));
    } catch (err) { logger.debug("load saved failed:", err?.message || err); }
  }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const toggleSave = useCallback(async (msg) => {
    try {
      const { data } = await api.post(`/chat/messages/${msg.id}/save`, {});
      setSavedIds((prev) => { const n = new Set(prev); if (data.saved) n.add(msg.id); else n.delete(msg.id); return n; });
      loadSaved();
      toast.success(data.saved ? "Saved for later" : "Removed from Saved");
    } catch (err) { toast.error(formatApiError(err)); }
  }, [loadSaved]);

  const openSavedItem = useCallback((item) => {
    setActive(item.scope_type === "channel" ? { type: "channel", id: item.scope_id } : { type: "dm", id: item.scope_id });
    setPendingHighlight(item.message_id);
    setSavedOpen(false);
  }, []);

  // Mention Inbox — every message that @-mentions me, across conversations.
  const loadMentions = useCallback(async () => {
    try { const { data } = await api.get("/chat/mentions"); setMentionList(data || []); }
    catch (err) { logger.debug("load mentions failed:", err?.message || err); }
  }, []);
  useEffect(() => { loadMentions(); }, [loadMentions]);

  const openMentionItem = useCallback((item) => {
    setActive(item.scope_type === "channel" ? { type: "channel", id: item.scope_id } : { type: "dm", id: item.scope_id });
    setPendingHighlight(item.message_id);
    setMentionOpen(false);
  }, []);

  const unreadMentions = mentionList.filter((m) => !mentionSeenAt || (m.created_at || "") > mentionSeenAt).length;
  const openMentions = useCallback(() => {
    setMentionOpen(true);
    loadMentions();
    const t = new Date().toISOString();
    localStorage.setItem("chat_mentions_seen_at", t);
    setMentionSeenAt(t);
  }, [loadMentions]);

  // Deep-link open: ?channel=<id>&msg=<id> or ?peer=<id>&msg=<id>. Select the
  // conversation and flag the message to scroll-to + flash once it loads.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const ch = sp.get("channel");
    const peer = sp.get("peer");
    const mid = sp.get("msg");
    if (!ch && !peer) return;
    if (ch) setActive({ type: "channel", id: ch });
    else if (peer) setActive({ type: "dm", id: peer });
    if (mid) setPendingHighlight(mid);
    // Clean the URL so a refresh doesn't keep re-jumping.
    try { window.history.replaceState({}, "", "/chat"); } catch (e) { /* noop */ }
  }, []);

  return (
    <div className="chat-surface flex h-[calc(100vh-7.5rem)] lg:h-[calc(100vh-3rem)] overflow-hidden" data-testid="chat-page">
      <div
        data-testid="chat-sidebar"
        style={{ ["--chat-w"]: `${sidebarWidth}px` }}
        className={`${active ? "hidden lg:flex" : "flex"} w-full lg:w-[var(--chat-w)] shrink-0 border-r border-slate-200 bg-white flex-col`}
      >
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="label-overline">Workspace</div>
          <div className="font-display text-xl font-bold text-slate-900">Chat</div>
        </div>
        <button
          type="button"
          onClick={() => setSavedOpen(true)}
          className="w-full flex items-center gap-2 px-5 py-2.5 border-b border-slate-200 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          data-testid="chat-saved-open"
        >
          <Bookmark size={15} className="text-[#F54800]" />
          <span className="font-medium">Saved</span>
          {savedIds.size > 0 && (
            <span className="ml-auto text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-[#F54800]/10 text-[#F54800]" data-testid="chat-saved-count">{savedIds.size}</span>
          )}
        </button>
        <button
          type="button"
          onClick={openMentions}
          className="w-full flex items-center gap-2 px-5 py-2.5 border-b border-slate-200 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          data-testid="chat-mentions-open"
        >
          <AtSign size={15} className="text-[#F54800]" />
          <span className="font-medium">Mentions</span>
          {unreadMentions > 0 && (
            <span className="ml-auto text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-[#F54800]/10 text-[#F54800]" data-testid="chat-mentions-count">{unreadMentions}</span>
          )}
        </button>
        <div className="flex-1 overflow-y-auto">
          {/* Starred — channels, groups and DMs the user pinned to the top */}
          {(() => {
            const chanById = {};
            [...activeChannels, ...archivedChannels].forEach((c) => { chanById[c.id] = c; });
            const starChans = [...starredChannels].map((id) => chanById[id]).filter(Boolean);
            const dmById = {};
            dmThreads.forEach((t) => { dmById[t.peer_id] = { id: t.peer_id, name: t.name, avatar: t.avatar, role: t.role }; });
            users.forEach((u) => { if (!dmById[u.id]) dmById[u.id] = { id: u.id, name: u.name, avatar: u.avatar, role: u.role }; });
            const starDms = [...starredDms].map((id) => dmById[id]).filter(Boolean);
            if (starChans.length === 0 && starDms.length === 0) return null;
            return (
              <>
                <div className="px-4 pt-3 pb-1"><div className="label-overline">Starred</div></div>
                <div className="px-2 space-y-0.5 pb-1">
                  {starChans.map((c) => {
                    const isActive = active?.type === "channel" && active.id === c.id;
                    const unread = unreadCh?.[c.id] || 0;
                    const hasUnread = unread > 0 && !isActive;
                    return (
                      <button key={`star-c-${c.id}`} onClick={() => setActive({ type: "channel", id: c.id })}
                        className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${isActive ? "bg-slate-900 text-white" : hasUnread ? "hover:bg-slate-100 text-[#F54800] font-semibold" : "hover:bg-slate-100 text-slate-700"}`}
                        data-testid={`starred-channel-${c.name}`}>
                        <Hash size={14} className={hasUnread ? "text-[#F54800]" : ""} />
                        <span className="flex-1 truncate">{c.name}</span>
                        <Star size={11} className="fill-amber-400 text-amber-500 shrink-0" />
                        {hasUnread && <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#F54800] text-white text-[10px] font-bold leading-none">{unread > 99 ? "99+" : unread}</span>}
                      </button>
                    );
                  })}
                  {starDms.map((u) => {
                    const isActive = active?.type === "dm" && active.id === u.id;
                    const muted = mutedDms.has(u.id);
                    const unread = unreadDm?.[u.id] || 0;
                    const hasUnread = unread > 0 && !isActive && !muted;
                    return (
                      <button key={`star-d-${u.id}`} onClick={() => selectConversation("dm", u.id)}
                        className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${isActive ? "bg-slate-900 text-white" : hasUnread ? "hover:bg-slate-100 text-[#F54800] font-semibold" : "hover:bg-slate-100 text-slate-700"}`}
                        data-testid={`starred-dm-${u.id}`}>
                        <MiniAvatar src={resolveAvatar(u.avatar)} name={u.name} size={22} userId={u.id} />
                        <span className="flex-1 truncate">{u.name}</span>
                        <Star size={11} className="fill-amber-400 text-amber-500 shrink-0" />
                        {hasUnread && <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#F54800] text-white text-[10px] font-bold leading-none">{unread > 99 ? "99+" : unread}</span>}
                      </button>
                    );
                  })}
                </div>
              </>
            );
          })()}
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="label-overline">Channels</div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button className="p-1 text-slate-400 hover:text-slate-900" data-testid="new-channel-btn"><Plus size={14} /></button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-[calc(100vw-2rem)] sm:max-w-md">
                <DialogHeader><DialogTitle className="font-display">Create channel or group</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <Input className="rounded-sm" placeholder="general" value={newCh.name} onChange={(e) => setNewCh({ ...newCh, name: e.target.value })} />
                  <Input className="rounded-sm" placeholder="Description (optional)" value={newCh.description} onChange={(e) => setNewCh({ ...newCh, description: e.target.value })} />
                  <Input className="rounded-sm" placeholder="Folder / Business Unit (optional)" value={newCh.folder} onChange={(e) => setNewCh({ ...newCh, folder: e.target.value })} data-testid="channel-folder-input" />
                  <button type="button" onClick={() => setNewCh((p) => ({ ...p, is_private: !p.is_private }))} className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-sm text-sm border transition-colors ${newCh.is_private ? "border-[#F54800]/40 bg-[#F54800]/10 text-[#B7350A]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid="channel-private-toggle">
                    <span className={`w-4 h-4 rounded-[4px] border flex items-center justify-center shrink-0 ${newCh.is_private ? "bg-[#F54800] border-[#F54800]" : "border-slate-300 bg-white"}`}>{newCh.is_private && <Check size={11} className="text-white" />}</span>
                    <Lock size={13} className="text-slate-500" /> Private group — only added members can see it
                  </button>
                  {newCh.is_private && (
                    <div className="border border-slate-200 rounded-sm">
                      <div className="px-2 py-1.5 text-[11px] font-medium text-slate-500 border-b border-slate-100">Add members ({newCh.member_ids.length})</div>
                      <div className="max-h-44 overflow-y-auto p-1 space-y-0.5">
                        {users.map((u) => {
                          const sel = newCh.member_ids.includes(u.id);
                          return (
                            <button type="button" key={u.id}
                              onClick={() => setNewCh((p) => ({ ...p, member_ids: sel ? p.member_ids.filter((x) => x !== u.id) : [...p.member_ids, u.id] }))}
                              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-left text-sm ${sel ? "bg-[#F54800]/10" : "hover:bg-slate-50"}`}
                              data-testid={`member-pick-${u.id}`}>
                              <MiniAvatar src={resolveAvatar(u.avatar)} name={u.name} size={22} userId={u.id} />
                              <span className="flex-1 truncate">{u.name}</span>
                              {u.role === "client" && <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 shrink-0">Client</span>}
                              {sel && <Check size={14} className="text-[#F54800]" />}
                            </button>
                          );
                        })}
                        {users.length === 0 && <div className="text-xs text-slate-400 px-2 py-2">No other users found.</div>}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter><Button onClick={createChannel} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>

          </div>
          <div className="px-2 space-y-0.5 pb-1">
            {(() => {
              // Channels section lists only un-foldered channels; foldered ones
              // live in their own "Folders" section below (peer to Direct messages).
              const src = showArchived ? archivedChannels : activeChannels;
              const ungrouped = src.filter((c) => !(c.folder || "").trim());
              const renderRow = (c) => {
                const isActive = active?.type === "channel" && active.id === c.id;
                const muted = mutedChannels.has(c.id);
                const unread = unreadCh?.[c.id] || 0;
                const hasUnread = unread > 0 && !isActive && !muted;
                return (
                  <button
                    key={c.id}
                    onClick={() => selectConversation("channel", c.id)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${
                      isActive ? "bg-slate-900 text-white" : hasUnread ? "hover:bg-slate-100 text-[#F54800] font-semibold" : c.archived ? "hover:bg-slate-100 text-slate-400" : "hover:bg-slate-100 text-slate-700"
                    } ${muted && !isActive ? "opacity-50" : ""}`}
                    data-testid={`channel-${c.name}`}
                  >
                    <Hash size={14} className={hasUnread ? "text-[#F54800]" : ""} />
                    <span className="flex-1 truncate">{c.name}</span>
                    {muted && <BellOff size={12} className="text-slate-400 shrink-0" data-testid={`channel-muted-${c.name}`} />}
                    {hasUnread && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#F54800] text-white text-[10px] font-bold leading-none" data-testid={`channel-unread-${c.name}`}>{unread > 99 ? "99+" : unread}</span>
                    )}
                  </button>
                );
              };
              return ungrouped.map(renderRow);
            })()}
          </div>
          {/* Phase 148 — Channel Archiving filter toggle */}
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="w-full text-left px-4 py-2 text-[11px] font-medium text-slate-400 hover:text-slate-600 inline-flex items-center gap-1.5"
            data-testid="toggle-archived-channels"
          >
            <Archive size={11} /> {showArchived ? "Hide" : "Show"} archived ({archivedChannels.length})
          </button>
          {/* Folders — channels grouped by Business Unit, their own section */}
          {(() => {
            const src = showArchived ? archivedChannels : activeChannels;
            const folders = {};
            src.forEach((c) => { const f = (c.folder || "").trim(); if (f) (folders[f] = folders[f] || []).push(c); });
            const folderNames = Object.keys(folders).sort((a, b) => a.localeCompare(b));
            if (folderNames.length === 0) return null;
            const renderRow = (c) => {
              const isActive = active?.type === "channel" && active.id === c.id;
              const muted = mutedChannels.has(c.id);
              const unread = unreadCh?.[c.id] || 0;
              const hasUnread = unread > 0 && !isActive && !muted;
              return (
                <button
                  key={c.id}
                  onClick={() => selectConversation("channel", c.id)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${
                    isActive ? "bg-slate-900 text-white" : hasUnread ? "hover:bg-slate-100 text-[#F54800] font-semibold" : c.archived ? "hover:bg-slate-100 text-slate-400" : "hover:bg-slate-100 text-slate-700"
                  } ${muted && !isActive ? "opacity-50" : ""}`}
                  data-testid={`channel-${c.name}`}
                >
                  <Hash size={14} className={hasUnread ? "text-[#F54800]" : ""} />
                  <span className="flex-1 truncate">{c.name}</span>
                  {muted && <BellOff size={12} className="text-slate-400 shrink-0" data-testid={`channel-muted-${c.name}`} />}
                  {hasUnread && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#F54800] text-white text-[10px] font-bold leading-none" data-testid={`channel-unread-${c.name}`}>{unread > 99 ? "99+" : unread}</span>
                  )}
                </button>
              );
            };
            return (
              <>
                <div className="px-4 pt-3 pb-1"><div className="label-overline">Folders</div></div>
                <div className="px-2 space-y-0.5 pb-1">
                  {folderNames.map((f) => {
                    const collapsed = !!collapsedFolders[f];
                    const chans = folders[f];
                    const folderUnread = chans.reduce((acc, c) => acc + ((active?.type === "channel" && active.id === c.id) ? 0 : (unreadCh?.[c.id] || 0)), 0);
                    return (
                      <div key={f} className="pt-0.5">
                        <button type="button" onClick={() => setCollapsedFolders((p) => ({ ...p, [f]: !p[f] }))} className="w-full flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800" data-testid={`folder-${f}`}>
                          {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                          <Folder size={12} className="text-slate-400" />
                          <span className="flex-1 text-left truncate">{f}</span>
                          {folderUnread > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-[#F54800] text-white text-[9px] font-bold leading-none">{folderUnread > 99 ? "99+" : folderUnread}</span>}
                        </button>
                        {!collapsed && <div className="space-y-0.5 mt-0.5">{chans.map(renderRow)}</div>}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
          {/* Direct messages — only conversations you've had, newest on top */}
          <div className="px-4 pt-3 pb-1 flex items-center justify-between">
            <div className="label-overline">Direct messages</div>
            <button
              type="button"
              onClick={() => { setDmPickerQuery(""); setDmPickerOpen(true); }}
              title="New message"
              className="p-1 rounded-sm text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              data-testid="dm-new-btn"
            >
              <Plus size={15} />
            </button>
          </div>
          <div className="px-2 space-y-0.5 pb-3">
            {(() => {
              // Conversations with real DM history, newest first (server sorts
              // by last message). If the user just opened a brand-new DM from
              // the picker (no messages yet), surface it on top so it stays
              // selected until the first message joins the real thread list.
              let list = dmThreads.map((t) => ({ id: t.peer_id, name: t.name, avatar: t.avatar, role: t.role }));
              if (active?.type === "dm" && !list.some((x) => x.id === active.id)) {
                const u = users.find((x) => x.id === active.id);
                if (u) list = [{ id: u.id, name: u.name, avatar: u.avatar, role: u.role }, ...list];
              }
              return list.map((u) => {
                const isActive = active?.type === "dm" && active.id === u.id;
                const muted = mutedDms.has(u.id);
                const unread = unreadDm?.[u.id] || 0;
                const hasUnread = unread > 0 && !isActive && !muted;
                return (
                  <button
                    key={u.id}
                    onClick={() => selectConversation("dm", u.id)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${
                      isActive
                        ? "bg-slate-900 text-white"
                        : hasUnread
                          ? "hover:bg-slate-100 text-[#F54800] font-semibold"
                          : "hover:bg-slate-100 text-slate-700"
                    } ${muted && !isActive ? "opacity-50" : ""}`}
                    data-testid={`dm-${u.id}`}
                  >
                    <MiniAvatar src={resolveAvatar(u.avatar)} name={u.name} size={22} userId={u.id} />
                    <span className="flex-1 truncate">{u.name}</span>
                    {muted && <BellOff size={12} className="text-slate-400 shrink-0" data-testid={`dm-muted-${u.id}`} />}
                    {u.role === "client" && !hasUnread && !muted && <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 shrink-0">Client</span>}
                    {hasUnread && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#F54800] text-white text-[10px] font-bold leading-none" data-testid={`dm-unread-${u.id}`}>
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </button>
                );
              });
            })()}
            {dmThreads.length === 0 && active?.type !== "dm" && (
              <div className="text-xs text-slate-400 px-3 py-2">No conversations yet — tap + to start one.</div>
            )}
          </div>
        </div>
      </div>

      {/* Drag-to-resize handle — desktop only */}
      <div
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize channel list"
        data-testid="chat-resize-handle"
        className="hidden lg:block w-1 cursor-col-resize bg-slate-100 hover:bg-[#F54800] active:bg-[#F54800] transition-colors"
        title="Drag to resize"
      />

      <div
        className={`${active ? "flex" : "hidden lg:flex"} flex-1 flex-col bg-[#F8F9FA] min-w-0 min-h-0 relative`}
        onTouchStart={onThreadTouchStart}
        onTouchEnd={onThreadTouchEnd}
        onDragOver={(e) => { if (active && e.dataTransfer?.types?.includes("Files")) { e.preventDefault(); setDragActive(true); } }}
        onDragLeave={(e) => { if (e.currentTarget === e.target) setDragActive(false); }}
        onDrop={(e) => { if (active) { e.preventDefault(); setDragActive(false); if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files); } }}
      >
        {dragActive && active && (
          <div className="absolute inset-0 z-40 bg-[#F54800]/10 border-2 border-dashed border-[#F54800] rounded-sm flex items-center justify-center pointer-events-none" data-testid="drop-overlay">
            <div className="bg-white px-4 py-2 rounded-sm shadow text-sm font-semibold text-[#B7350A] inline-flex items-center gap-2"><Paperclip size={16} /> Drop files to attach</div>
          </div>
        )}
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-white border-b border-slate-200 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setActive(null)}
              className="lg:hidden p-1.5 -ml-1 rounded-sm text-slate-600 hover:bg-slate-100"
              aria-label="Back to channels"
              data-testid="chat-back"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0">
              <div className="label-overline">{active?.type === "channel" ? "Channel" : "Direct message"}</div>
              <div className="font-display text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2 truncate">
                {active?.type === "channel" ? <Hash size={16} /> : <UserIcon size={16} />}
                <span className="truncate">{activeMeta?.name || "Select a conversation"}</span>
                {activeMeta?.archived && <Tag tone="slate">archived</Tag>}
                {isActiveMuted && <Tag tone="yellow">muted</Tag>}
                {isAnnouncements && <Tag tone="orange">read-only · Founders/Admins post</Tag>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => setSearchOpen(true)} className="p-1.5 rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-600" title="Search messages" data-testid="chat-search-open"><Search size={14} /></button>
            {pinnedMessages.length > 0 && (
              <button
                onClick={() => setPinnedOpen((v) => !v)}
                className={`p-1.5 rounded-sm border text-xs font-semibold inline-flex items-center gap-1 ${pinnedOpen ? "bg-[#F54800]/10 border-[#F54800]/40 text-[#F54800]" : "border-slate-200 hover:bg-slate-50 text-slate-600"}`}
                title="Pinned messages"
                data-testid="pinned-toggle"
              >
                <Pin size={13} /> {pinnedMessages.length}
              </button>
            )}
            {active?.type === "channel" && (
              <>
                <button onClick={exportChannel} className="p-1.5 rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-600" title="Download history" data-testid="export-channel"><Download size={14} /></button>
                <button onClick={openMembers} className="p-1.5 rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-600" title="Members / add people" data-testid="manage-members"><Users size={14} /></button>
                <button onClick={renameChannel} className="p-1.5 rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-600" title="Rename" data-testid="rename-channel"><Pencil size={14} /></button>
                {can("chat_create_channel") && (
                  <>
                    <button onClick={toggleArchive} className="p-1.5 rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-600" title={activeMeta?.archived ? "Restore" : "Archive"} data-testid="archive-channel">
                      {activeMeta?.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                    </button>
                    <button onClick={deleteChannel} className="p-1.5 rounded-sm border border-slate-200 hover:bg-red-50 text-red-600" title="Delete" data-testid="delete-channel"><Trash2 size={14} /></button>
                  </>
                )}
              </>
            )}
            {active && (
              <button
                onClick={toggleStar}
                className={`p-1.5 rounded-sm border ${isActiveStarred ? "bg-amber-50 border-amber-300 text-amber-500" : "border-slate-200 hover:bg-slate-50 text-slate-600"}`}
                title={isActiveStarred ? "Remove from Starred" : "Add to Starred"}
                data-testid="star-toggle"
              >
                <Star size={14} className={isActiveStarred ? "fill-amber-400 text-amber-500" : ""} />
              </button>
            )}
            {active && (
              <button
                onClick={toggleMute}
                className={`p-1.5 rounded-sm border text-slate-600 ${isActiveMuted ? "bg-amber-50 border-amber-300 text-amber-700" : "border-slate-200 hover:bg-slate-50"}`}
                title={isActiveMuted ? "Unmute this conversation" : "Mute this conversation"}
                data-testid="mute-toggle"
              >
                {isActiveMuted ? <BellOff size={14} /> : <Bell size={14} />}
              </button>
            )}
            <Tag tone="green">● live</Tag>
          </div>
        </div>

            <Dialog open={membersOpen} onOpenChange={setMembersOpen}>
              <DialogContent className="rounded-sm max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="manage-members-dialog">
                <DialogHeader><DialogTitle className="font-display">Members</DialogTitle></DialogHeader>
                <Input className="rounded-sm mt-2" placeholder="Folder / Business Unit (optional)" value={editFolder} onChange={(e) => setEditFolder(e.target.value)} data-testid="members-folder-input" />
                <button type="button" onClick={() => setEditPrivate((v) => !v)} className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-sm text-sm border transition-colors mt-2 ${editPrivate ? "border-[#F54800]/40 bg-[#F54800]/10 text-[#B7350A]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid="members-private-toggle">
                  <span className={`w-4 h-4 rounded-[4px] border flex items-center justify-center shrink-0 ${editPrivate ? "bg-[#F54800] border-[#F54800]" : "border-slate-300 bg-white"}`}>{editPrivate && <Check size={11} className="text-white" />}</span>
                  <Lock size={13} className="text-slate-500" /> Private group — only added members can see it
                </button>
                <div className="border border-slate-200 rounded-sm mt-2">
                  <div className="px-2 py-1.5 text-[11px] font-medium text-slate-500 border-b border-slate-100">Members ({editMembers.length} selected + you)</div>
                  <div className="max-h-56 overflow-y-auto p-1 space-y-0.5">
                    {users.map((u) => {
                      const sel = editMembers.includes(u.id);
                      return (
                        <button type="button" key={u.id}
                          onClick={() => setEditMembers((p) => sel ? p.filter((x) => x !== u.id) : [...p, u.id])}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-left text-sm ${sel ? "bg-[#F54800]/10" : "hover:bg-slate-50"}`}
                          data-testid={`member-edit-${u.id}`}>
                          <MiniAvatar src={resolveAvatar(u.avatar)} name={u.name} size={22} userId={u.id} />
                          <span className="flex-1 truncate">{u.name}</span>
                              {u.role === "client" && <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 shrink-0">Client</span>}
                          {sel && <Check size={14} className="text-[#F54800]" />}
                        </button>
                      );
                    })}
                    {users.length === 0 && <div className="text-xs text-slate-400 px-2 py-2">No other users found.</div>}
                  </div>
                </div>
                <DialogFooter><Button onClick={saveMembers} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
              <DialogContent className="rounded-sm max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="rename-dialog">
                <DialogHeader><DialogTitle className="font-display">Rename channel</DialogTitle></DialogHeader>
                <Input autoFocus className="rounded-sm mt-2" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveRename(); }} data-testid="rename-input" />
                <DialogFooter><Button onClick={saveRename} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
        <Dialog open={dmPickerOpen} onOpenChange={setDmPickerOpen}>
          <DialogContent className="rounded-sm max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="dm-picker-dialog">
            <DialogHeader><DialogTitle className="font-display">New message</DialogTitle></DialogHeader>
            <Input autoFocus placeholder="Search people…" value={dmPickerQuery} onChange={(e) => setDmPickerQuery(e.target.value)} className="rounded-sm mt-1" data-testid="dm-picker-search" />
            <div className="mt-2 max-h-[50vh] overflow-y-auto space-y-0.5">
              {users
                .filter((u) => (u.name || "").toLowerCase().includes(dmPickerQuery.trim().toLowerCase()))
                .map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => { setActive({ type: "dm", id: u.id }); setDmPickerOpen(false); }}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-sm text-sm hover:bg-slate-100 text-slate-700"
                    data-testid={`dm-picker-${u.id}`}
                  >
                    <MiniAvatar src={resolveAvatar(u.avatar)} name={u.name} size={22} userId={u.id} />
                    <span className="flex-1 truncate">{u.name}</span>
                    {u.role === "client" && <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 shrink-0">Client</span>}
                  </button>
                ))}
              {users.filter((u) => (u.name || "").toLowerCase().includes(dmPickerQuery.trim().toLowerCase())).length === 0 && (
                <div className="text-xs text-slate-400 px-3 py-4 text-center">No people found.</div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={deleteOpen} onOpenChange={(o) => { if (!o && !deleteBusy) setDeleteOpen(false); }}>
          <DialogContent className="rounded-sm max-w-[calc(100vw-2rem)] sm:max-w-sm" data-testid="delete-channel-dialog">
            <DialogHeader><DialogTitle className="font-display">Delete channel?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600 mt-1">
              Delete <span className="font-medium text-slate-900">#{activeMeta?.name}</span>? All messages will be removed permanently. This cannot be undone.
            </p>
            <DialogFooter className="mt-4 gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setDeleteOpen(false)} disabled={deleteBusy} data-testid="delete-cancel-btn">Cancel</Button>
              <Button onClick={confirmDeleteChannel} disabled={deleteBusy} className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="delete-confirm-btn">{deleteBusy ? "Deleting…" : "Delete channel"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={!!taskMsg} onOpenChange={(o) => { if (!o) setTaskMsg(null); }}>
          <DialogContent className="rounded-sm max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="convert-task-dialog">
            <DialogHeader><DialogTitle className="font-display">Convert to task</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <label className="text-[11px] font-medium text-slate-500">Title</label>
                <Input className="rounded-sm mt-1" value={taskForm.title} onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))} data-testid="task-title-input" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500">Details</label>
                <textarea rows={3} className="w-full rounded-sm mt-1 border border-slate-300 bg-white px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40" value={taskForm.description} onChange={(e) => setTaskForm((f) => ({ ...f, description: e.target.value }))} data-testid="task-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-medium text-slate-500">Due date</label>
                  <Input type="date" className="rounded-sm mt-1" value={taskForm.due_date} onChange={(e) => setTaskForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="task-due-input" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-500">Assignee</label>
                  <select className="w-full rounded-sm mt-1 border border-slate-300 bg-white px-2 py-[9px] text-sm" value={taskForm.assignee_id} onChange={(e) => setTaskForm((f) => ({ ...f, assignee_id: e.target.value }))} data-testid="task-assignee-select">
                    <option value="">Me</option>
                    {users.filter((u) => u.role !== "client").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500">Project (optional)</label>
                <select className="w-full rounded-sm mt-1 border border-slate-300 bg-white px-2 py-[9px] text-sm" value={taskForm.project_id} onChange={(e) => setTaskForm((f) => ({ ...f, project_id: e.target.value }))} data-testid="task-project-select">
                  <option value="">No project</option>
                  {taskProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <DialogFooter><Button onClick={saveTask} disabled={taskSaving} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">{taskSaving ? "Creating…" : "Create task"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={!!lightboxImage} onOpenChange={(o) => { if (!o) setLightboxImage(null); }}>
          <DialogContent className="rounded-sm max-w-[92vw] sm:max-w-3xl p-2 bg-white" data-testid="image-lightbox">
            <DialogHeader className="sr-only"><DialogTitle>Image preview</DialogTitle></DialogHeader>
            {lightboxImage && (
              <div className="flex flex-col gap-2">
                <img src={lightboxImage.url} alt={lightboxImage.name} className="w-full max-h-[75vh] object-contain rounded-sm" />
                <div className="flex items-center justify-between px-1 pb-1 gap-2">
                  <span className="text-xs text-slate-500 truncate">{lightboxImage.name}</span>
                  <a href={lightboxImage.url} download={lightboxImage.name} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#F54800] inline-flex items-center gap-1 hover:underline shrink-0"><Download size={13} /> Download</a>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <Dialog open={searchOpen} onOpenChange={(o) => { setSearchOpen(o); if (!o) { setSearchQ(""); setSearchResults([]); } }}>
          <DialogContent className="rounded-sm max-w-lg" data-testid="chat-search-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Search size={16} /> Search messages</DialogTitle></DialogHeader>
            <div className="mt-1">
              <Input autoFocus value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search your channels and DMs…" className="rounded-sm" data-testid="chat-search-input" />
              <div className="mt-3 max-h-[50vh] overflow-y-auto divide-y divide-slate-100" data-testid="chat-search-results">
                {searchLoading && <div className="py-6 text-center text-xs text-slate-400">Searching…</div>}
                {!searchLoading && searchQ.trim().length >= 2 && searchResults.length === 0 && (
                  <div className="py-6 text-center text-xs text-slate-400">No messages found.</div>
                )}
                {!searchLoading && searchQ.trim().length < 2 && (
                  <div className="py-6 text-center text-xs text-slate-400">Type at least 2 characters to search.</div>
                )}
                {!searchLoading && searchResults.map((r) => (
                  <button key={r.id} onClick={() => openSearchResult(r)} className="w-full text-left py-2 px-1 hover:bg-slate-50 rounded-sm" data-testid={`chat-search-result-${r.id}`}>
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-0.5">
                      {r.is_dm ? <UserIcon size={11} /> : <Hash size={11} />}
                      <span className="font-medium">{r.is_dm ? (r.peer_name || "Direct message") : (r.channel_name || "channel")}</span>
                      <span>· {r.sender_name}</span>
                      {r.created_at && <span>· {new Date(r.created_at).toLocaleDateString()}</span>}
                    </div>
                    <div className="text-sm text-slate-700 truncate">{r.text}</div>
                  </button>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={savedOpen} onOpenChange={setSavedOpen}>
          <DialogContent className="rounded-sm max-w-lg" data-testid="chat-saved-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Bookmark size={16} className="text-[#F54800]" /> Saved for later</DialogTitle></DialogHeader>
            <div className="mt-1 max-h-[55vh] overflow-y-auto divide-y divide-slate-100" data-testid="chat-saved-list">
              {savedList.length === 0 && (
                <div className="py-8 text-center text-xs text-slate-400">No saved messages yet. Hover any message and tap the bookmark to keep it here.</div>
              )}
              {savedList.map((item) => (
                <div key={item.id} className="py-2 px-1 flex items-start gap-2 group" data-testid={`chat-saved-item-${item.message_id}`}>
                  <button onClick={() => openSavedItem(item)} className="flex-1 min-w-0 text-left hover:bg-slate-50 rounded-sm px-1 py-0.5" data-testid={`chat-saved-jump-${item.message_id}`}>
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-0.5">
                      {item.scope_type === "dm" ? <UserIcon size={11} /> : <Hash size={11} />}
                      <span className="font-medium truncate">{item.scope_label}</span>
                      {item.sender_name && item.sender_name !== item.scope_label && <span className="shrink-0">· {item.sender_name}</span>}
                      {item.saved_at && <span className="shrink-0">· saved {new Date(item.saved_at).toLocaleDateString()}</span>}
                    </div>
                    <div className="text-sm text-slate-700 line-clamp-2">{item.text || "📎 attachment"}</div>
                  </button>
                  <button
                    onClick={() => toggleSave({ id: item.message_id })}
                    title="Remove from Saved"
                    className="p-1 rounded-sm text-[#F54800] hover:bg-[#F54800]/10 shrink-0"
                    data-testid={`chat-saved-remove-${item.message_id}`}
                  >
                    <BookmarkCheck size={15} />
                  </button>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={mentionOpen} onOpenChange={setMentionOpen}>
          <DialogContent className="rounded-sm max-w-lg" data-testid="chat-mentions-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><AtSign size={16} className="text-[#F54800]" /> Mentions</DialogTitle></DialogHeader>
            <div className="mt-1 max-h-[55vh] overflow-y-auto divide-y divide-slate-100" data-testid="chat-mentions-list">
              {mentionList.length === 0 && (
                <div className="py-8 text-center text-xs text-slate-400">No mentions yet. When someone @-mentions you, it'll show up here.</div>
              )}
              {mentionList.map((item) => (
                <button
                  key={item.message_id}
                  onClick={() => openMentionItem(item)}
                  className="w-full py-2 px-1 text-left hover:bg-slate-50 rounded-sm block"
                  data-testid={`chat-mention-item-${item.message_id}`}
                >
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-0.5">
                    {item.scope_type === "dm" ? <UserIcon size={11} /> : <Hash size={11} />}
                    <span className="font-medium truncate">{item.scope_label}</span>
                    {item.sender_name && item.sender_name !== item.scope_label && <span className="shrink-0">· {item.sender_name}</span>}
                    {item.created_at && <span className="shrink-0">· {new Date(item.created_at).toLocaleDateString()}</span>}
                  </div>
                  <div className="text-sm text-slate-700 line-clamp-2">{item.text || "📎 attachment"}</div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Pinned messages panel */}
        {pinnedOpen && pinnedMessages.length > 0 && (
          <div className="px-4 sm:px-6 py-2 bg-[#FFF8F3] border-b border-[#F54800]/20 space-y-1" data-testid="pinned-panel">
            {pinnedMessages.map((m) => (
              <button
                key={m.id}
                onClick={() => document.getElementById(`msg-${m.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className="w-full text-left text-xs text-slate-700 hover:text-[#F54800] truncate flex items-center gap-1.5"
                data-testid={`pinned-item-${m.id}`}
              >
                <Pin size={10} className="text-[#F54800] shrink-0" /> <span className="font-medium">{m.sender_name}:</span> {m.text || "📎 attachment"}
              </button>
            ))}
          </div>
        )}

        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-2 relative" data-testid="chat-messages">
          {firstUnreadId && (
            <button
              type="button"
              onClick={jumpToUnread}
              className="sticky top-0 z-10 mx-auto flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F54800] text-white text-[11px] font-semibold shadow-md hover:bg-[#D63E00] transition-colors"
              data-testid="jump-to-unread"
            >
              <ArrowDownCircle size={13} /> Jump to unread
            </button>
          )}
          {active && !olderLoaded && (
            <div className="text-center pb-2">
              <button type="button" onClick={loadOlderMessages} className="text-[11px] text-slate-500 hover:text-[#F54800] underline underline-offset-2" data-testid="chat-load-older">
                Show older messages (archived)
              </button>
            </div>
          )}
          {messages.map((m, i) => (
            <React.Fragment key={m.id}>
              {(i === 0 || !sameDay(messages[i - 1].created_at, m.created_at)) && (
                <ChatDateDivider date={m.created_at} messages={messages} />
              )}
              {m.id === firstUnreadId && (
                <div className="flex items-center gap-2 py-1" data-testid="new-messages-divider">
                  <div className="flex-1 h-px bg-[#F54800]/40" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#F54800]">New messages</span>
                  <div className="flex-1 h-px bg-[#F54800]/40" />
                </div>
              )}
              <ChatMessage
                msg={m}
                mine={m.sender_id === user.id}
                currentUserId={user.id}
                quoted={m.reply_to_id ? messagesById[m.reply_to_id] : null}
                canShare={canShareToClient}
                onAnnotate={(msg, att) => setAnnotateTarget({ msg, att })}
                onPin={pinMessage}
                onSave={toggleSave}
                saved={savedIds.has(m.id)}
                onShareToggle={shareToggle}
                onReply={setReplyTo}
                onReact={reactToMessage}
                onEdit={editMessage}
                onCopyLink={copyMessageLink}
                userNameById={userNameById}
                userAvatarById={userAvatarById}
                onOpenImage={setLightboxImage}
                onConvertTask={user?.role === "client" ? null : openTaskDialog}
              />
            </React.Fragment>
          ))}
          {messages.length === 0 && active && (
            <div className="text-center text-sm text-slate-400 mt-10">No messages yet. Say hi 👋</div>
          )}
          {dmSeen && (
            <div className="flex justify-end pr-1 pt-0.5" data-testid="dm-seen-indicator">
              <span className="text-[10px] text-slate-400 inline-flex items-center gap-1"><CheckCheck size={11} className="text-[#F54800]" /> Seen</span>
            </div>
          )}
        </div>

        {/* Ephemeral typing indicator */}
        {active && Object.keys(typingUsers).length > 0 && (() => {
          const names = Object.values(typingUsers);
          let label;
          if (names.length === 1) label = `${names[0]} is typing…`;
          else if (names.length === 2) label = `${names[0]} and ${names[1]} are typing…`;
          else label = `${names[0]}, ${names[1]} and ${names.length - 2} ${names.length - 2 === 1 ? "other" : "others"} are typing…`;
          return (
            <div className="px-4 sm:px-6 pb-1 pt-0.5 flex items-center gap-1.5 text-xs text-slate-500" data-testid="typing-indicator">
              <span className="flex gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
              </span>
              <span className="italic truncate">{label}</span>
            </div>
          );
        })()}

        {active && !activeMeta?.archived && !composerLocked && (
          <div className="bg-white border-t border-slate-200">
            {/* Reply-to strip */}
            {replyTo && (
              <div className="px-4 sm:px-6 pt-2 flex items-center justify-between gap-2 text-xs text-slate-500 border-l-2 border-[#F54800] mx-4 sm:mx-6 mt-2" data-testid="reply-preview">
                <span className="truncate">Replying to <b>{replyTo.sender_name}</b>: {replyTo.text || "📎 attachment"}</span>
                <button type="button" onClick={() => setReplyTo(null)} className="text-slate-400 hover:text-slate-700 shrink-0"><X size={12} /></button>
              </div>
            )}
            {/* Attachment preview chips (supports several per message) */}
            {attachments.length > 0 && (
              <div className="px-4 sm:px-6 pt-2 flex items-center gap-2 flex-wrap" data-testid="attachment-preview">
                {attachments.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1 text-xs text-slate-700 max-w-[200px]">
                    {a.type === "image" ? <img src={a.url} alt="" className="w-6 h-6 rounded-sm object-cover shrink-0" /> : <FileText size={13} className="text-[#F54800] shrink-0" />}
                    <span className="truncate">{a.name}</span>
                    <button type="button" onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600 shrink-0" data-testid={`attachment-remove-${i}`}><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}

            <div className="px-4 sm:px-6 py-3 sm:py-4 lg:pr-48 flex items-end gap-2 relative z-10">
              {/* Emoji picker — insert any emoji into the message */}
              <div className="relative shrink-0 self-end pb-1">
                <button
                  type="button"
                  onClick={() => setComposerEmojiOpen((v) => !v)}
                  title="Emoji"
                  className="p-2 rounded-sm text-slate-400 hover:text-[#F54800] hover:bg-slate-50"
                  data-testid="emoji-btn"
                >
                  <SmilePlus size={16} />
                </button>
                {composerEmojiOpen && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setComposerEmojiOpen(false)} />
                    <div className="absolute bottom-full left-0 mb-2 z-30">
                      <EmojiPicker onPick={(emoji) => { richRef.current?.insertText(emoji); setComposerEmojiOpen(false); }} />
                    </div>
                  </>
                )}
              </div>
              <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.avif,.stp,.step,.sldprt,.sldasm,.f3d,.stl,.igs,.iges,.obj,.3mf,.dwg,.dxf,.zip,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt" className="hidden" onChange={handleFileSelect} data-testid="chat-file-input" />
              <button
                type="button"
                onClick={pickFile}
                disabled={uploading}
                title="Attach images or files (you can pick several)"
                className="p-2 rounded-sm text-slate-400 hover:text-[#F54800] hover:bg-slate-50 shrink-0 self-end pb-1"
                data-testid="attach-btn"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
              </button>
              <div className="flex-1 min-w-0">
                <RichTextInput
                  ref={richRef}
                  users={(users || []).filter((u) => u.id !== user.id)}
                  onTyping={(has) => sendTypingSignal(!has)}
                  onSubmit={send}
                  onPasteFiles={(files) => uploadFiles(files)}
                  placeholder={`Message ${active.type === "channel" ? "#" + (activeMeta?.name || "") : activeMeta?.name || ""}  ·  @ to tag · :emoji: · ⌘B`}
                  testIdPrefix="chat"
                  inputTestId="chat-input"
                />
              </div>
              <Button onClick={send} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white relative z-20 self-end" data-testid="send-message"><Send size={14} /></Button>
            </div>
          </div>
        )}
        {active && activeMeta?.archived && (
          <div className="px-4 sm:px-6 py-3 bg-slate-50 border-t border-slate-200 text-center text-xs text-slate-400" data-testid="archived-notice">
            This channel is archived — read-only.
          </div>
        )}
        {active && !activeMeta?.archived && composerLocked && (
          <div className="px-4 sm:px-6 py-3 bg-[#FFF8F3] border-t border-[#F54800]/20 text-center text-xs text-[#B7350A] font-medium" data-testid="announcements-readonly-notice">
            Only Founders and Admins can post studio-wide announcements here.
          </div>
        )}
      </div>

      <AnnotationModal
        open={!!annotateTarget}
        onClose={() => setAnnotateTarget(null)}
        imageUrl={annotateTarget?.att?.url}
        imageName={annotateTarget?.att?.name}
        onSaved={handleAnnotationSaved}
      />
    </div>
  );
}
